import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { seoArticles, seoArticleContents } from "@/mocks/articles";
import { ArticleSchema, BreadcrumbListSchema } from "@/components/base/SchemaOrg";

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleMeta = seoArticles.find((a) => a.slug === slug);
  const articleContent = slug ? seoArticleContents[slug] : undefined;

  if (!articleMeta || !articleContent) {
    return (
      <main>
        <Navbar />
        <div className="w-full py-20 bg-background-50 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link to="/" className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer">
            <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
            Back to Homepage
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const articleUrl = `https://gtbuyspreadsheet.skin/article/${slug}`;
  const excerptWithoutHtml = articleMeta.excerpt.replace(/<[^>]*>/g, "");

  return (
    <main>
      <title>{articleContent.title}</title>
      <meta name="description" content={excerptWithoutHtml.slice(0, 160)} />
      <meta name="keywords" content="gtbuy spreadsheet, kakobuy spreadsheet, superbuy spreadsheet, order tracking spreadsheet, proxy shopping spreadsheet, spreadsheet order management" />
      <link rel="canonical" href={articleUrl} />
      <ArticleSchema
        headline={articleContent.title}
        description={excerptWithoutHtml}
        url={articleUrl}
        datePublished="2026-07-01"
        dateModified="2026-07-01"
        imageUrl={articleContent.headerImage}
      />
      <BreadcrumbListSchema
        items={[
          { name: "Home", url: "https://gtbuyspreadsheet.skin" },
          { name: articleContent.title, url: articleUrl },
        ]}
      />
      <Navbar />

      <article className="w-full py-10 md:py-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <header className="mb-10">
            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <span>/</span>
              <span className="text-foreground-700">{articleContent.title}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {articleContent.title}
            </h1>
            <p className="text-sm text-foreground-500">
              Published: {articleContent.date} &bull; Updated: {articleContent.date} &bull; {articleContent.readTime}
            </p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <div className="relative w-full h-[300px] md:h-[400px]">
              <img
                src={articleContent.headerImage}
                alt={articleContent.title}
                title={articleContent.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="prose-custom space-y-8 text-foreground-800 leading-relaxed">
            {articleContent.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className={`text-sm md:text-base ${pIdx > 0 ? 'mt-3' : ''}`}>
                    {para}
                  </p>
                ))}
              </section>
            ))}

            <section className="p-6 rounded-xl bg-primary-50 border border-primary-200">
              <h2 className="font-heading text-lg font-bold text-primary-800 mb-3">
                Ready to Transform Your Order Management?
              </h2>
              <p className="text-sm text-primary-700 mb-4">
                Stop struggling with scattered order data across multiple platforms. Start using the <strong>GTBuy spreadsheet</strong> system today and experience the difference that centralized, automated tracking makes for your international purchasing workflow.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-shopping-cart-2-line w-4 h-4 flex items-center justify-center"></i>
                  gtbuy sheet
                </a>
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 border border-primary-300 text-primary-700 font-semibold text-sm hover:bg-primary-100 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                  Back to Homepage
                </Link>
              </div>
            </section>
          </div>

          <nav className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
              >
                Visit gtbuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </nav>
        </div>
      </article>

      <Footer />
    </main>
  );
}